// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    numberOfZones = "numberOfZones",
    tabletColumns = "tabletColumns",
    mobileColumns = "mobileColumns",
    gap = "gap",
}

interface Props extends WidgetProps {
    fields: {
        [fields.numberOfZones]: number;
        [fields.tabletColumns]: number;
        [fields.mobileColumns]: number;
        [fields.gap]: number;
    };
}

export interface CustomRowStyles {
    row?: InjectableCss;
    rowOffset?: InjectableCss;
    column?: InjectableCss;
}

export const customRowStyles: CustomRowStyles = {
    row: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${({ gap }: any) => `${gap}px`};
        `,
    },
    column: {
        css: css`
            ${({ numberOfZones, tabletColumns, mobileColumns }: any) => css`
                flex-grow: 1;
                flex-shrink: 1;
                flex-basis: calc(100% / ${numberOfZones} - 2%);

                ${xScreen("lte", "md")} {
                    /* in > 1 cols view, last row might contain less items, hence they grow and break the design */
                    flex-grow: ${tabletColumns === 1 ? 1 : 0};
                    flex-basis: calc(100% / ${tabletColumns} - 2%);
                }

                ${xScreen("lte", "xs")} {
                    flex-grow: ${tabletColumns === 1 ? 1 : 0};
                    flex-basis: calc(100% / ${mobileColumns} - 2%);
                }
            `}
        `,
    },
};

const CustomRow: React.FunctionComponent<Props> = (props: Props) => {
    const styles = customRowStyles;

    const {
        fields: { numberOfZones, tabletColumns, mobileColumns, gap },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < numberOfZones; x += 1) {
        columnElements.push(
            <StyledWrapper key={x} {...styles.column} {...({ numberOfZones, tabletColumns, mobileColumns } as any)}>
                <Zone contentId={id} zoneName={`Content-${x}`} />
            </StyledWrapper>,
        );
    }

    return (
        <StyledWrapper className="row-container" {...styles.row} {...({ gap } as any)}>
            {columnElements}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CustomRow,
    definition: {
        group: "XNGAGE" as any,
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.numberOfZones,
                displayName: "Number of zones to be created",
                editorTemplate: "IntegerField",
                defaultValue: 3,
                fieldType: "General",
                min: 1,
                sortOrder: 1,
            },
            {
                name: fields.tabletColumns,
                displayName: "tablet columns",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                tooltip: "how many columns one row in a tablet screen should view",
                min: 1,
                sortOrder: 2,
            },
            {
                name: fields.mobileColumns,
                displayName: "mobile columns",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                tooltip: "how many columns one row in a mobile screen should view",
                min: 1,
                sortOrder: 3,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                min: 0,
            },
        ],
    },
};

export default widgetModule;
